import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../../store/authStore';
import { useSupabaseAuth } from '../../hooks/useSupabaseAuth';
import { AlertCircle, Eye, EyeOff, Loader2, ArrowLeft } from 'lucide-react';
import { AuthLayout } from '../../components/auth/AuthLayout';
import { OAuthButtonRow } from '../../components/auth/OAuthButtonRow';
import styles from './auth-form.module.css';

export function Login() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { login, isLoading, error, clearError, isAuthenticated } = useAuthStore();
  const { signInWithGoogle, signInWithTwitter, signInWithDiscord, loading: oauthLoading } = useSupabaseAuth();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/');
    }
  }, [isAuthenticated, navigate]);

  // Clear error on unmount
  useEffect(() => {
    return () => clearError();
  }, [clearError]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      await login(email, password);
      // Navigation handled by useEffect above
    } catch (error) {
      // Error handled by store
    }
  };

  return (
    <AuthLayout>
        {/* Header */}
        <div className={styles.header}>
          <h1 className={styles.title}>
            {t('auth.welcomeBack', 'Welcome Back')}
          </h1>
          <p className={styles.subtitle}>
            {t('auth.loginSubtitle', 'Sign in to access your wallet tracker')}
          </p>
        </div>

        {/* Error Message */}
        {error && (
          <div role="alert" className={styles.errorBanner}>
            <AlertCircle size={16} color="var(--error)" />
            <span className={styles.errorText}>{error}</span>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit}>
          {/* Email */}
          <div className={styles.field}>
            <label className={styles.label}>
              {t('auth.email', 'Email')}
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              placeholder="you@example.com"
              className={styles.input}
            />
          </div>

          {/* Password */}
          <div className={styles.fieldTight}>
            <div className={styles.labelRow}>
              <label className={styles.labelInline}>{t('auth.password', 'Password')}</label>
              <Link to="/auth/forgot-password" className={styles.forgotLink}>
                {t('auth.hints.forgotPassword', 'Forgot password?')}
              </Link>
            </div>
            <div className={styles.passwordWrapper}>
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                placeholder="••••••••"
                className={styles.inputWithIcon}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className={styles.passwordToggle}
                aria-label={
                  showPassword
                    ? t('auth.hidePassword', { defaultValue: 'Hide password' })
                    : t('auth.showPassword', { defaultValue: 'Show password' })
                }
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={isLoading}
            className={
              isLoading
                ? `${styles.submitButton} ${styles.submitButtonDisabled}`
                : styles.submitButton
            }
          >
            {isLoading ? (
              <>
                <Loader2 size={18} className={styles.spinner} />
                {t('auth.loggingIn', 'Signing in...')}
              </>
            ) : (
              t('auth.actions.login', 'Sign In')
            )}
          </button>
        </form>

        {/* Divider */}
        <div className={styles.divider}>
          <div className={styles.dividerLine} />
          <span className={styles.dividerLabel}>
            {t('auth.orContinueWith', 'or continue with')}
          </span>
          <div className={styles.dividerLine} />
        </div>

        {/* Social Login Buttons */}
        <OAuthButtonRow
          mode="login"
          onGoogle={signInWithGoogle}
          onTwitter={signInWithTwitter}
          onDiscord={signInWithDiscord}
          loading={oauthLoading}
        />

        {/* Sign Up Link */}
        <div className={styles.signupRow}>
          <span className={styles.signupHint}>
            {t('auth.noAccount', "Don't have an account?")}{' '}
          </span>
          <Link to="/auth/register" className={styles.signupLink}>
            {t('auth.actions.signup', 'Sign up')}
          </Link>
        </div>

        {/* Back to Home */}
        <div className={styles.backRow}>
          <Link to="/" className={styles.backLink}>
            <ArrowLeft size={14} />
            {t('auth.backToHome', 'Back to Home')}
          </Link>
        </div>
    </AuthLayout>
  );
}
